import { Flex, Grid } from "@radix-ui/themes";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import {
  createContext,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Editor } from "tldraw";

import TicketCanvas from "./components/ticket-canvas";
import TicketHeader from "./components/TicketHeader";
import { ticketRoute } from "./route";

import { useUI } from "../../../../contexts/ui";
import { useRateTicket } from "../../../../hooks/useRateTicket";
import { isNil } from "../../../../utils/fp";
import { useAppSelector } from "../../../../utils/hooks";
import { EditableDoc as EditableDocRT } from "../../components/EditableDoc/EditableDocRT";
import { CommentDrawer } from "../components/comment-drawer";
import { RepliesCollapseProvider } from "../components/comment-drawer/Comment";

type TicketContextValues = {
  /**
   * Whether or not the user has initiated an AI Review from anywhere on the
   * page.
   */
  hasInitiatedAiReview: boolean;
  /**
   * Handler to set whether the user has initiated an AI Review
   */
  setHasInitiatedAiReview(value: boolean): void;
  /**
   * The TLDraw Editor object, used by other components on the page, such as
   * `CommentDrawer`, and to create helper functions to navigate to shapes (e.g.
   * if the user clicks a page in the document thumbnail sidebar in doc mode).
   * This value must only be set by a child descendant that implements TLDraw
   * and no other component should set this value. If no TLDraw instance has
   * been loaded, `editor` will be `undefined`.
   */
  editor?: Editor;
  /**
   * Sets the editor object. It is important to remember that only TLDraw
   * should set this value where it is implemented. There should not be multiple
   * references to TLDraw on the page, and this value must not be set by anything
   * other than one instance of TLDraw.
   */
  setEditor(editor?: Editor): void;
  /**
   * Some to-be-removed controls for video mode.
   */
  videoRef: RefObject<{
    handleCommentClicked: (_seekTime: number) => void;
    duration: number;
  }>;
};

const TicketContext = createContext({} as TicketContextValues);
export const useTicket = () => useContext(TicketContext);

export default function View() {
  // Router methods
  const { ticketId } = ticketRoute.useParams();
  const { rating, integration } = ticketRoute.useSearch();

  // Slices
  const { value: ticket, isLoading } = useAppSelector((state) => state.ticket);

  // Mutations
  const rateTicketMutation = useRateTicket();

  // Analytics
  const posthog = usePostHog();
  const { notify } = useUI();

  // Local state
  const [hasInitiatedAiReview, setHasInitiatedAiReview] = useState(false);
  const [editor, setEditor] = useState<Editor>();
  // TODO: remove this because refs and imperative handlers abstract the code
  // in ways that reduce readability and make the code harder to understand.
  const videoRef = useRef<{
    handleCommentClicked: (_seekTime: number) => void;
    duration: number;
  }>(null);

  // Effect chain

  useEffect(() => {
    const context = {
      ticketId,
      ticketTitle: ticket?.title,
    };
    posthog.register(context);

    return () => {
      Object.keys(context).forEach((key) => {
        posthog.unregister(key);
      });
    };
  }, []);

  // Handle rating if provided in URL
  useEffect(() => {
    if (rating && ticketId && rating.toString() != ticket?.rating?.toString()) {
      const ratingNum = parseInt(rating.toString(), 10);
      if (!isNaN(ratingNum) && ratingNum >= 1 && ratingNum <= 10) {
        rateTicketMutation.mutate({ ticketId, rating: ratingNum });
        posthog.capture(ratingNum > 1 ? "positive_rating" : "negative_rating", {
          ticketId,
          rating: ratingNum,
        });
        notify({
          title: "Thank you for your feedback!",
          message: "We'll use it to improve our service.",
        });
      }
    }
  }, [rating, ticketId]);

  // Handle integration if provided in URL
  useEffect(() => {
    if (integration && ticketId) {
      posthog.capture("ticket_opened_from_integration", {
        ticketId,
        ticketTitle: ticket?.title,
      });
    }
  }, [integration, ticketId]);

  const isDemo = useMemo(
    () => ticketId == "editabledoc" || ticketId == "editabledoc2",
    [ticketId],
  );
  const docType = useMemo(() => {
    if (isDemo) {
      return ticketId == "editabledoc2" ? "joke" : "redline";
    }
    return undefined;
  }, [isDemo, ticketId]);
  /////
  // No hooks beyond this point
  /////

  // If for some reason the API responded with a nullish ticket, or the parser
  // failed and return undefined.

  if (isNil(ticket) && !isDemo) {
    // demo purposes only for check of editabledoc
    if (isLoading) {
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          height="100vh"
          width="100vw"
          position="fixed"
          className="text-center"
        >
          Loading&hellip;
        </Flex>
      );
    }
    throw { status: 404 };
  }

  return (
    <TicketContext.Provider
      value={{
        hasInitiatedAiReview,
        setHasInitiatedAiReview,
        editor,
        setEditor,
        videoRef,
      }}
    >
      <RepliesCollapseProvider>
        <Grid
          className={cx(
            "h-screen grid-rows-[auto_1fr] overflow-hidden [&:has([data-mode=video])]:bg-puntt-neutral-gray-5",
            {
              "grid-cols-[1fr_auto]": !isDemo,
            },
          )}
          data-testid="ticket-layout"
        >
          <TicketHeader />
          {isDemo ? <EditableDocRT docType={docType} /> : <TicketCanvas />}
          {!isDemo && <CommentDrawer editor={editor} videoRef={videoRef} />}
        </Grid>
      </RepliesCollapseProvider>
    </TicketContext.Provider>
  );
}
